import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";
import "./styles.css";

const ViewActionModal = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "",
  showInput = false,
  inputLabel = "Input",
  inputPlaceholder = "",
  inputValue = "",
  onInputChange,
  showCheckbox = false,
  checkboxLabel = "",
  checkboxValue = false,
  onCheckboxChange,
  showCheckboxTooltip = false,
  checkboxTooltipText = "",
  cancelButtonText = "Cancel",
  confirmButtonText = "Confirm",
  confirmButtonColor = "primary",
  maxWidth = "sm",
  fullWidth = true,
}) => {
  const [localInputValue, setLocalInputValue] = useState(inputValue);
  const [localCheckboxValue, setLocalCheckboxValue] = useState(checkboxValue);

  // Sync local state with prop value when modal opens
  useEffect(() => {
    if (open) {
      setLocalInputValue(inputValue);
      setLocalCheckboxValue(checkboxValue);
    }
  }, [open, inputValue, checkboxValue]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalInputValue(newValue);
    onInputChange?.(newValue);
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const newValue = e.target.checked;
    setLocalCheckboxValue(newValue);
    onCheckboxChange?.(newValue);
  };

  // Handle confirm action
  const handleConfirm = () => {
    if (showInput && showCheckbox) {
      onConfirm?.(localInputValue, localCheckboxValue);
    } else if (showInput) {
      onConfirm?.(localInputValue);
    } else if (showCheckbox) {
      onConfirm?.(localCheckboxValue);
    } else {
      onConfirm?.();
    }
  };

  // Handle close/cancel action
  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      className="view-action-modal"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography component="span" sx={{ 
            color: '#01151D',
            fontFamily: 'Source Sans Pro',
            fontSize: '20px',
            fontWeight: 400,
            lineHeight: '28px',
            letterSpacing: '-0.1px'
          }}>
            {title}
          </Typography>
          <IconButton
            onClick={handleClose}
            className="view-action-modal-close-btn"
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ px: '24px'}}>
        {message && (
          <Typography variant="body2" className="view-action-modal-message" sx={{ 
            lineHeight: '1',
            marginBottom: '16px'
          }}>
            {message}
          </Typography>
        )}
        {showInput && (
          <TextField
            fullWidth
            size="small"
            label={inputLabel}
            placeholder={inputPlaceholder}
            value={localInputValue}
            onChange={handleInputChange}
            variant="outlined"
            autoFocus
          />
        )}
        {showCheckbox && (
          <FormControlLabel
            control={
              <Checkbox
                checked={localCheckboxValue}
                onChange={handleCheckboxChange}
                size="small"
                sx={{ 
                  color: '#6B7280',
                  '&.Mui-checked': {
                    color: '#0977A0',
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                  {checkboxLabel}
                </Typography>
                {showCheckboxTooltip && (
                  <CustomTooltip
                    title={checkboxTooltipText}
                    arrow
                    placement="top"
                    enterDelay={300}
                    leaveDelay={200}
                    maxWidth="227px"
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: '16px', color: '#637177', cursor: 'pointer' }}
                    />
                  </CustomTooltip>
                )}
              </Box>
            }
            sx={{ mt: showInput ? 2 : 0 }}
          />
        )}
      </Box>
      <DialogActions sx={{ px: '24px', pb: '24px' }}>
        <Button onClick={handleClose} className="view-action-modal-cancel-btn">
          {cancelButtonText}
        </Button>
        <Button
          variant="contained"
          color={confirmButtonColor}
          onClick={handleConfirm}
          className="view-action-modal-confirm-btn"
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ViewActionModal);
